import {
  platformConsoleCreateAdminContractVersion,
  platformConsoleCreateAdminRoutePathPattern,
  platformConsoleCreateTenantContractVersion,
  platformConsoleTenantsContractVersion,
  platformConsoleTenantsRoutePath,
  type PlatformConsoleCreateAdminRequestV1,
  type PlatformConsoleCreateAdminResponseV1,
  type PlatformConsoleCreateTenantRequestV1,
  type PlatformConsoleCreateTenantResponseV1,
  type PlatformConsoleTenantsResponseV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";

function resolveSessionFetchBaseUrl(config: MobileRuntimeConfig): string {
  if (config.sessionApiBaseUrl) {
    return config.sessionApiBaseUrl;
  }

  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin;
  }

  throw new Error("platform-console-session-base-url-unavailable");
}

function buildPlatformConsoleTenantsUrl(config: MobileRuntimeConfig): string {
  return new URL(
    platformConsoleTenantsRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPlatformConsoleCreateAdminUrl(
  config: MobileRuntimeConfig,
  tenantKey: string,
): string {
  return new URL(
    platformConsoleCreateAdminRoutePathPattern.replace(
      "{tenantKey}",
      encodeURIComponent(tenantKey),
    ),
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

async function resolvePlatformConsoleErrorMessage(
  response: Response,
  fallbackMessage: string,
): Promise<string> {
  try {
    const errorPayload = (await response.json()) as { message?: unknown };

    if (
      typeof errorPayload.message === "string" &&
      errorPayload.message.trim()
    ) {
      return errorPayload.message.trim();
    }
  } catch {
    // Preserve the status-based fallback when the server body is unavailable.
  }

  return fallbackMessage;
}

export async function fetchPlatformConsoleTenants(
  config: MobileRuntimeConfig,
  signal?: AbortSignal,
): Promise<PlatformConsoleTenantsResponseV1> {
  const response = await fetch(buildPlatformConsoleTenantsUrl(config), {
    credentials: "include",
    headers: {
      accept: "application/json",
    },
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw new Error(`platform-console-tenants-failed-${response.status}`);
  }

  const payload = (await response.json()) as PlatformConsoleTenantsResponseV1;

  if (payload.schemaVersion !== platformConsoleTenantsContractVersion) {
    throw new Error("platform-console-tenants-schema-mismatch");
  }

  return payload;
}

export async function createPlatformConsoleTenant(
  config: MobileRuntimeConfig,
  input: {
    tenantKey: string;
    tenantSlug: string;
    displayName: string;
  },
): Promise<PlatformConsoleCreateTenantResponseV1> {
  const requestBody: PlatformConsoleCreateTenantRequestV1 = {
    schemaVersion: platformConsoleCreateTenantContractVersion,
    tenantKey: input.tenantKey,
    tenantSlug: input.tenantSlug,
    displayName: input.displayName,
  };

  const response = await fetch(buildPlatformConsoleTenantsUrl(config), {
    body: JSON.stringify(requestBody),
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(
      await resolvePlatformConsoleErrorMessage(
        response,
        `platform-console-create-tenant-failed-${response.status}`,
      ),
    );
  }

  const payload =
    (await response.json()) as PlatformConsoleCreateTenantResponseV1;

  if (payload.schemaVersion !== platformConsoleCreateTenantContractVersion) {
    throw new Error("platform-console-create-tenant-schema-mismatch");
  }

  return payload;
}

export async function createPlatformConsoleFirstAdmin(
  config: MobileRuntimeConfig,
  input: {
    tenantKey: string;
    email: string;
    displayName: string;
    temporaryPassword: string;
  },
): Promise<PlatformConsoleCreateAdminResponseV1> {
  const requestBody: PlatformConsoleCreateAdminRequestV1 = {
    schemaVersion: platformConsoleCreateAdminContractVersion,
    tenantKey: input.tenantKey,
    email: input.email,
    displayName: input.displayName,
    temporaryPassword: input.temporaryPassword,
  };

  const response = await fetch(
    buildPlatformConsoleCreateAdminUrl(config, input.tenantKey),
    {
      body: JSON.stringify(requestBody),
      credentials: "include",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      method: "POST",
    },
  );

  if (!response.ok) {
    throw new Error(
      await resolvePlatformConsoleErrorMessage(
        response,
        `platform-console-create-admin-failed-${response.status}`,
      ),
    );
  }

  const payload =
    (await response.json()) as PlatformConsoleCreateAdminResponseV1;

  if (payload.schemaVersion !== platformConsoleCreateAdminContractVersion) {
    throw new Error("platform-console-create-admin-schema-mismatch");
  }

  return payload;
}
